/**
 * Generic list section component for displaying calls (traces or evaluations).
 * Renders a list of call items with loading and empty states.
 */

import {Button} from '@wandb/components';
import {IconName} from '@wandb/weave/components/Icon';
// @todo: Migrate to 'PotentialRoadmap' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {LoadingDots} from '@wandb/weave/components/LoadingDots';
import {useWFHooks} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import {Query} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientInterface/query';
import {CallSchema} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/wfDataModelHooksInterface';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useMemo} from 'react';
import {useHistory} from 'react-router-dom';

import {
  CALL_QUERY_COLUMNS,
  LIST_CONTAINER_HEIGHT,
  LIST_ITEM_LIMIT,
} from '../constants';
import {EmptyState} from './EmptyState';

interface CallsListSectionProps {
  entityName: string;
  projectName: string;
  title: string;
  viewAllPath: string;
  query?: Query;
  filter: Record<string, any>;
  renderItem: (call: CallSchema) => React.ReactNode;
  emptyMessage: string;
  emptySubtitle: string;
  emptyIcon: IconName;
  emptyIconColor: string;
  emptyIconBgGradient: string;
}

export const CallsListSection: React.FC<CallsListSectionProps> = ({
  entityName,
  projectName,
  title,
  viewAllPath,
  query,
  filter,
  renderItem,
  emptyMessage,
  emptySubtitle,
  emptyIcon,
  emptyIconColor,
  emptyIconBgGradient,
}) => {
  const {useCalls} = useWFHooks();
  const history = useHistory();
  const sortBy = useMemo(
    () => [{field: 'started_at', direction: 'desc' as const}],
    []
  );

  const calls = useCalls({
    entity: entityName,
    project: projectName,
    filter,
    query,
    limit: LIST_ITEM_LIMIT,
    sortBy,
    columns: CALL_QUERY_COLUMNS,
    includeCosts: true,
  });

  const hasCalls = calls.result && calls.result.length > 0;

  return (
    <Tailwind>
      <div>
        <div className="mb-16 flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-moon-500">
            {title}
          </h3>
          {hasCalls && (
            <Button
              endIcon="forward-next"
              variant="ghost"
              onClick={() => {
                history.push(
                  `/${entityName}/${projectName}/weave/${viewAllPath}`
                );
              }}
              data-test="launch-drawer-back"
              size="sm">
              View all
            </Button>
          )}
        </div>

        {calls.loading ? (
          <div
            className="flex items-center justify-center rounded-xl border border-moon-250 bg-white shadow-sm"
            style={{height: `${LIST_CONTAINER_HEIGHT}px`}}>
            <LoadingDots />
          </div>
        ) : hasCalls ? (
          <div
            className="overflow-hidden rounded-xl border border-moon-250 bg-white shadow-sm"
            style={{height: `${LIST_CONTAINER_HEIGHT}px`}}>
            <div className="divide-y divide-moon-100">
              {calls.result
                // Filter out calls without started_at (incomplete/corrupted data)
                ?.filter(call => call.traceCall?.started_at)
                .slice(0, LIST_ITEM_LIMIT)
                .map(call => renderItem(call))}
            </div>
          </div>
        ) : (
          <div style={{height: `${LIST_CONTAINER_HEIGHT}px`}}>
            <EmptyState
              icon={emptyIcon}
              message={emptyMessage}
              subtitle={emptySubtitle}
              iconColor={emptyIconColor}
              iconBgGradient={emptyIconBgGradient}
            />
          </div>
        )}
      </div>
    </Tailwind>
  );
};
